// components/NotificationCenter.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Badge,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Button,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    WarningAmber as WarningIcon,
    Info as InfoIcon,
    Check as CheckIcon,
    Warning as AlertIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import {
    collection,
    query,
    where,
    getDocs,
    updateDoc,
    doc,
    orderBy,
    limit,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const NotificationCenter = ({ orgId }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const notificationsRef = collection(db, 'organizations', orgId, 'notifications');
            const q = query(
                notificationsRef,
                orderBy('timestamp', 'desc'),
                limit(20)
            );

            const snapshot = await getDocs(q);
            const notificationData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setNotifications(notificationData);
            setUnreadCount(notificationData.filter(n => n.status === 'unread').length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // Set up a refresh interval
        const interval = setInterval(fetchNotifications, 60000); // Refresh every minute

        return () => clearInterval(interval);
    }, [orgId]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            const notificationRef = doc(db, 'organizations', orgId, 'notifications', notificationId);
            await updateDoc(notificationRef, {
                status: 'read',
                readAt: serverTimestamp()
            });

            // Update local state
            setNotifications(prevNotifications =>
                prevNotifications.map(n =>
                    n.id === notificationId ? { ...n, status: 'read' } : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const promises = notifications
                .filter(n => n.status === 'unread')
                .map(n => {
                    const notificationRef = doc(db, 'organizations', orgId, 'notifications', n.id);
                    return updateDoc(notificationRef, {
                        status: 'read',
                        readAt: serverTimestamp()
                    });
                });

            await Promise.all(promises);

            // Update local state
            setNotifications(prevNotifications =>
                prevNotifications.map(n => ({ ...n, status: 'read' }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'warning':
                return <WarningIcon color="warning" />;
            case 'success':
                return <CheckIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'alert':
                return <AlertIcon color="error" />;
            case 'info':
            default:
                return <InfoIcon color="info" />;
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? 'notification-popover' : undefined;

    return (
        <>
            <IconButton
                color="inherit"
                aria-describedby={id}
                onClick={handleClick}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { width: 400, maxHeight: 500 }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Notifications</Typography>
                    {unreadCount > 0 && (
                        <Button size="small" onClick={handleMarkAllAsRead}>
                            Mark all as read
                        </Button>
                    )}
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">No notifications</Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notification) => (
                            <React.Fragment key={notification.id}>
                                <ListItem
                                    alignItems="flex-start"
                                    sx={{
                                        bgcolor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                                        transition: 'background-color 0.3s'
                                    }}
                                    secondaryAction={
                                        notification.status === 'unread' && (
                                            <Button
                                                size="small"
                                                onClick={() => handleMarkAsRead(notification.id)}
                                            >
                                                Mark as read
                                            </Button>
                                        )
                                    }
                                >
                                    <Box sx={{ mr: 1, mt: 0.5 }}>
                                        {getNotificationIcon(notification.type)}
                                    </Box>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2">{notification.title}</Typography>
                                                {notification.status === 'unread' && (
                                                    <Chip size="small" label="New" color="primary" />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    {notification.message}
                                                </Typography>
                                                <Typography
                                                    component="div"
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {notification.timestamp?.toDate().toLocaleString() || 'Just now'}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    );
};

export default NotificationCenter;